import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  type Unsubscribe,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions, firestoreDb } from './firebase'
import type {
  NotificationRecord,
  NotificationWorkspaceAudience,
  UserNotificationPreferences,
  WorkspaceNotificationPreferences,
} from '../types/schema'
import { DEFAULT_CATEGORY_TOGGLES } from '../utils/notificationCategories'

const resolveTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export const createDefaultUserNotificationPreferences = (
  userId: string,
  timezone = resolveTimezone(),
): UserNotificationPreferences => ({
  userId,
  timezone,
  categories: structuredClone(DEFAULT_CATEGORY_TOGGLES),
  digest: { enabled: true },
  quietMode: {
    enabled: false,
    startHour: 22,
    endHour: 8,
  },
  bundleRepeats: false,
})

const normalizeUserNotificationPreferences = (
  userId: string,
  raw: Partial<UserNotificationPreferences> & Record<string, unknown>,
): UserNotificationPreferences => {
  const defaults = createDefaultUserNotificationPreferences(userId)
  const rawCategories = (raw.categories ?? {}) as Record<string, unknown>
  const categories = Object.fromEntries(
    Object.entries(defaults.categories).map(([key, value]) => [
      key,
      rawCategories[key] && typeof rawCategories[key] === 'object'
        ? {
            inApp: typeof (rawCategories[key] as Record<string, unknown>).inApp === 'boolean'
              ? Boolean((rawCategories[key] as Record<string, unknown>).inApp) : value.inApp,
            push: typeof (rawCategories[key] as Record<string, unknown>).push === 'boolean'
              ? Boolean((rawCategories[key] as Record<string, unknown>).push) : value.push,
            email: typeof (rawCategories[key] as Record<string, unknown>).email === 'boolean'
              ? Boolean((rawCategories[key] as Record<string, unknown>).email) : value.email,
            digest: typeof (rawCategories[key] as Record<string, unknown>).digest === 'boolean'
              ? Boolean((rawCategories[key] as Record<string, unknown>).digest) : value.digest,
          }
        : value,
    ]),
  ) as UserNotificationPreferences['categories']

  const quiet = (raw.quietMode ?? {}) as Partial<UserNotificationPreferences['quietMode']>
  const quietMode: UserNotificationPreferences['quietMode'] = {
    enabled: Boolean(quiet.enabled ?? defaults.quietMode.enabled),
    startHour: clampHour(Number(quiet.startHour ?? defaults.quietMode.startHour), defaults.quietMode.startHour),
    endHour: clampHour(Number(quiet.endHour ?? defaults.quietMode.endHour), defaults.quietMode.endHour),
  }

  return {
    userId,
    timezone: typeof raw.timezone === 'string' && raw.timezone ? raw.timezone : defaults.timezone,
    categories,
    digest: { enabled: (raw.digest as { enabled?: boolean } | undefined)?.enabled !== false },
    quietMode,
    bundleRepeats: typeof raw.bundleRepeats === 'boolean' ? raw.bundleRepeats : defaults.bundleRepeats ?? false,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  }
}

const clampHour = (value: number, fallback: number): number => {
  if (!Number.isFinite(value)) return fallback
  return Math.max(0, Math.min(23, Math.round(value)))
}

const preferencesPath = (userId: string) =>
  doc(firestoreDb, 'user_preferences', userId, 'notification_settings', 'default')

export const loadUserNotificationPreferences = async (userId: string) => {
  const snapshot = await getDoc(preferencesPath(userId))
  if (!snapshot.exists()) return createDefaultUserNotificationPreferences(userId)
  return normalizeUserNotificationPreferences(userId, snapshot.data() as Partial<UserNotificationPreferences>)
}

export const saveUserNotificationPreferences = async (
  _userId: string,
  preferences: UserNotificationPreferences,
) => {
  const callable = httpsCallable<UserNotificationPreferences, UserNotificationPreferences>(
    firebaseFunctions,
    'updateNotificationPreferences',
  )
  await callable(preferences)
}

export const subscribeToUserNotificationPreferences = (
  userId: string,
  callback: (preferences: UserNotificationPreferences | null) => void,
): Unsubscribe => {
  let cancelled = false

  void loadUserNotificationPreferences(userId)
    .then((loaded) => {
      if (!cancelled) callback(loaded)
    })
    .catch((error) => {
      console.warn('[notificationService] Failed to load user notification preferences', error)
    })

  const unsubscribe = onSnapshot(
    preferencesPath(userId),
    () => {
      void loadUserNotificationPreferences(userId)
        .then((loaded) => {
          if (!cancelled) callback(loaded)
        })
        .catch((error) => {
          console.warn('[notificationService] Failed to load user notification preferences', error)
        })
    },
    (error) => {
      console.warn('[notificationService] Failed to listen to user notification preferences', error)
    },
  )

  return () => {
    cancelled = true
    unsubscribe()
  }
}

export const mutateUserNotifications = async (
  action: 'READ' | 'CLEAR' | 'CLICK' | 'RESTORE',
  notificationIds: string[],
) => {
  const callable = httpsCallable<
    { action: 'READ' | 'CLEAR' | 'CLICK' | 'RESTORE'; notificationIds: string[] },
    { updated: number; deepLink: string | null }
  >(firebaseFunctions, 'mutateNotifications')
  let updated = 0
  let deepLink: string | null = null
  for (let index = 0; index < notificationIds.length; index += 200) {
    const result = (await callable({ action, notificationIds: notificationIds.slice(index, index + 200) })).data
    updated += result.updated
    deepLink = result.deepLink ?? deepLink
  }
  return { updated, deepLink }
}

export const restoreUserNotifications = async (notificationIds: string[]) =>
  mutateUserNotifications('RESTORE', notificationIds)

export const removeDigestItem = async (digestId: string, itemIndex: number): Promise<void> => {
  const callable = httpsCallable<
    { action: 'REMOVE_DIGEST_ITEM'; digestId: string; itemIndex: number },
    { updated: number; deepLink: null }
  >(firebaseFunctions, 'mutateNotifications')
  await callable({ action: 'REMOVE_DIGEST_ITEM', digestId, itemIndex })
}

export const mutateAllUserNotifications = async (action: 'READ' | 'CLEAR') => {
  const callable = httpsCallable<
    { action: 'READ' | 'CLEAR'; all: true },
    { updated: number; deepLink: null }
  >(firebaseFunctions, 'mutateNotifications')
  return (await callable({ action, all: true })).data
}

export const createDefaultWorkspaceNotificationPreferences = (
  workspaceId: string,
  audience: NotificationWorkspaceAudience,
): WorkspaceNotificationPreferences => ({
  workspaceId,
  audience,
  masterEnabled: true,
  defaults: {
    blockers: { inApp: true, push: false, email: false, digest: false },
    mentions: { inApp: true, push: false, email: false, digest: false },
    dueDates: { inApp: true, push: false, email: false, digest: false },
    raids: { inApp: true, push: false, email: false, digest: false },
    credits: { inApp: true, push: false, email: false, digest: false },
    reports: { inApp: true, push: false, email: false, digest: false },
    permissions: { inApp: true, push: false, email: false, digest: false },
    activityDigest: { inApp: true, push: false, email: false, digest: false },
  },
  batching: {
    enabled: true,
    nonUrgentDelayMinutes: 10,
    editAggregationWindowSeconds: 60,
  },
})

export const loadWorkspaceNotificationPreferences = async (
  workspaceId: string,
  audience: NotificationWorkspaceAudience,
) => {
  const ref = doc(firestoreDb, 'workspaces', workspaceId, 'notification_preferences', audience)
  const snapshot = await getDoc(ref)
  if (!snapshot.exists()) return createDefaultWorkspaceNotificationPreferences(workspaceId, audience)
  return {
    ...createDefaultWorkspaceNotificationPreferences(workspaceId, audience),
    ...snapshot.data(),
  } as WorkspaceNotificationPreferences
}

export const saveWorkspaceNotificationPreferences = async (
  workspaceId: string,
  audience: NotificationWorkspaceAudience,
  preferences: WorkspaceNotificationPreferences,
) => {
  await setDoc(doc(firestoreDb, 'workspaces', workspaceId, 'notification_preferences', audience), {
    ...preferences,
    workspaceId,
    audience,
    updatedAt: serverTimestamp(),
  }, { merge: true })
}

export const getNotificationCategory = (notification: Pick<NotificationRecord, 'eventType' | 'entityType' | 'category'>) => {
  if (notification.category) return notification.category
  if (notification.eventType === 'FORM_REQUESTED') return 'UPDATES'
  if (notification.eventType === 'MENTION_CREATED') return 'MENTIONS'
  if (notification.eventType === 'INVITE_RECEIVED') return 'SECURITY'
  if (notification.eventType?.includes('RAID')) return 'GAMIFICATION'
  if (notification.eventType?.includes('CREDIT')) return 'SUBSCRIPTION'
  if (notification.eventType?.includes('ACHIEVEMENT') || notification.eventType?.includes('COSMETIC') || notification.eventType?.includes('STREAK')) return 'GAMIFICATION'
  if (notification.eventType?.includes('REPORT')) return 'UPDATES'
  return 'UPDATES'
}
